import type { ReactNode } from 'react';
import { Layout } from './Layout';
import { PageHead } from './PageHead';

interface PolicyPageProps {
  seoTitle: string;
  seoDescription: string;
  canonicalPath: string;
  title: string;
  effectiveDate: string;
  lastUpdated: string;
  children: ReactNode;
}

/**
 * Long-form prose wrapper used by /privacy and /terms. Same chrome
 * (header + footer via Layout) as the rest of the marketing site, but
 * with a narrower max-width + serif prose styling so a 3,000-word legal
 * doc reads cleanly. Section + sub-section components below are scoped
 * for use inside the prose body.
 */
export function PolicyPage({
  seoTitle,
  seoDescription,
  canonicalPath,
  title,
  effectiveDate,
  lastUpdated,
  children,
}: PolicyPageProps) {
  return (
    <Layout>
      <PageHead title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} />
      <article className="mx-auto max-w-3xl px-6 pt-16 pb-24 md:pt-24">
        <header className="mb-12 border-b pb-8" style={{ borderColor: 'var(--border)' }}>
          <h1
            className="text-4xl font-bold tracking-tight md:text-5xl"
            style={{ color: 'var(--text)' }}
          >
            {title}
          </h1>
          <p className="mt-4 text-sm" style={{ color: 'var(--text-muted)' }}>
            <strong>Effective date:</strong> {effectiveDate} &nbsp;·&nbsp;{' '}
            <strong>Last updated:</strong> {lastUpdated}
          </p>
        </header>
        <div className="policy-prose space-y-6 leading-relaxed" style={{ color: 'var(--text)' }}>
          {children}
        </div>
      </article>
    </Layout>
  );
}

export function PolicySection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2
        className="mt-10 mb-4 text-2xl font-bold tracking-tight md:text-3xl"
        style={{ color: 'var(--text)' }}
      >
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function PolicySubsection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-6">
      <h3
        className="mb-3 text-lg font-semibold tracking-tight md:text-xl"
        style={{ color: 'var(--text)' }}
      >
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export function PolicyList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc space-y-2 pl-6">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}
